import { useElements, useStripe } from '@stripe/react-stripe-js';
import React from 'react';
import { toast } from 'sonner';

import { Dialog } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import Button from 'creative-components/CustomButtons/Button';

import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import SimpleModalHeader from 'components/SimpleModalHeader/SimpleModalHeader';
import StripePaymentElement from 'components/StripePaymentElement/StripePaymentElement';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import {
  createUserPaymentMethod,
  getBrokeragePaymentMethodSetupIntent, getUserPaymentMethodSetupIntent, updateBrokeragePaymentMethod,
} from 'utils/api';
import { showAPIErrorAlert } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  stripeCardElement: {
    margin: '50px 0',
  },
  buttonContainer: {
    textAlign: 'center',
  },
  button: {
    borderRadius: '8px',
    padding: '8px 12px',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    textTransform: 'uppercase',
  },
}));

const AddPaymentMethodButton = ({ center = false }) => {
  const classes = useStyles();
  const stripe = useStripe();
  const elements = useElements();
  const { reloadUserInfo } = useUserInfoContext();
  const { isBrokerageAdmin } = useAuthDataContext();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext();

  const [modalOpen, setModalOpen] = React.useState(false);

  const savePaymentMethod = async () => {
    try {
      showLoadingIndicatorModal();

      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast.error(submitError.message);
        return;
      }

      // TODO: REMOVE BROKERAGE PAYMENT STUFF???
      const getSetupIntentCall = isBrokerageAdmin ? getBrokeragePaymentMethodSetupIntent : getUserPaymentMethodSetupIntent;
      const { setupIntentClientSecret } = await getSetupIntentCall();

      const cardSetupResult = await stripe.confirmSetup({
        elements,
        clientSecret: setupIntentClientSecret,
        redirect: 'if_required', // Only want redirect for redirect-based payments
        // confirmParams: {
        //   return_url: 'https://example.com/order/123/complete',
        // },
      });

      if (cardSetupResult.error) {
        // Don't do the API request, have the Stripe CC field go red
        hideLoadingIndicatorModal();

        toast.error(cardSetupResult.error.message);

        return;
      }

      const newPaymentMethodId = cardSetupResult.setupIntent.payment_method;

      const updatePaymentMethodCall = isBrokerageAdmin ? updateBrokeragePaymentMethod : createUserPaymentMethod;
      await updatePaymentMethodCall(newPaymentMethodId);

      toast.success('Your payment method has been saved.');

      setModalOpen(false);

      reloadUserInfo();
    } catch (err) {
      showAPIErrorAlert(err);
      console.error(err);
    } finally {
      hideLoadingIndicatorModal();
    }
  };

  const onModalClose = () => setModalOpen(false);

  return (
    <>
      {modalOpen && (
        <Dialog
          open
          scroll="body"
          keepMounted
          maxWidth="sm"
          onClose={onModalClose}
          className={classes.updateContainer}
        >
          <SimpleModalHeader onClose={onModalClose} title="Add Payment Method" />

          <div className={classes.stripeCardElement}>
            <StripePaymentElement />
          </div>

          <div className={classes.buttonContainer}>
            <Button
              round
              color="primary"
              onClick={savePaymentMethod}
            >
              Save
            </Button>
          </div>
        </Dialog>
      )}

      <div style={{ textAlign: center ? 'center' : 'left' }}>
        <Button
          color="warning"
          className={classes.button}
          onClick={() => setModalOpen(true)}
        >
          + Add Payment Method
        </Button>
      </div>
    </>
  );
};

export default AddPaymentMethodButton;
